import { launchColors, PillStyle } from '../launchTheme';
import { AlcoholQuick, AlcoholState, AlcoholStatus } from './alcoholTypes';

export interface Tone { dot: string; fg: string; bg: string }

// Två toner som saknas i launchTheme, i samma stil: stark, mörk för text och ljus för yta.
const rose: Tone = { dot: '#C07A8A', fg: '#7E3F4E', bg: '#F1DDE1' };
const orange: Tone = { dot: '#C0703F', fg: '#8A4A22', bg: '#F3DFD0' };

export const STATUS_TONES: Record<AlcoholStatus, Tone> = {
  approved: { dot: launchColors.moss, fg: launchColors.mossDark, bg: launchColors.mossLight },
  coming: { dot: launchColors.warningMarker, fg: launchColors.warningText, bg: launchColors.warningBg },
};

export const STATE_TONES: Record<AlcoholState, Tone> = {
  current: { dot: launchColors.navy, fg: launchColors.navy, bg: launchColors.softNavy },
  paused: { dot: launchColors.textMuted, fg: launchColors.textSecondary, bg: launchColors.neutralBg },
  customer: { dot: launchColors.petrol, fg: launchColors.petrolDark, bg: launchColors.petrolLight },
  cancelled: { dot: launchColors.dangerText, fg: launchColors.dangerText, bg: launchColors.dangerBg },
};

export const CATEGORY_TONES: Record<string, Tone> = {
  red: { dot: launchColors.plumDark, fg: launchColors.plumDark, bg: launchColors.plumLight },
  rose,
  white: { dot: launchColors.sand, fg: launchColors.sandDark, bg: launchColors.sandLight },
  sparkling: { dot: launchColors.warningMarker, fg: launchColors.warningText, bg: launchColors.warningBg },
  orange,
  dessert: { dot: launchColors.sandDark, fg: launchColors.sandDark, bg: launchColors.sandLight },
  spirit: { dot: launchColors.navyLight, fg: launchColors.navy, bg: launchColors.softNavy },
  beer: { dot: launchColors.textMuted, fg: launchColors.textSecondary, bg: launchColors.neutralBg },
  nonAlcoholic: { dot: launchColors.petrol, fg: launchColors.petrolDark, bg: launchColors.petrolLight },
  other: { dot: launchColors.textMuted, fg: launchColors.textSecondary, bg: launchColors.neutralBg },
};

const CATEGORY_RULES: Array<[RegExp, string]> = [
  [/alkoholfri/, 'nonAlcoholic'],
  [/mousserande|cava|champagne|prosecco/, 'sparkling'],
  [/orange/, 'orange'],
  [/ros[ée]/, 'rose'],
  [/rött|röd/, 'red'],
  [/vitt/, 'white'],
  [/port|dessert/, 'dessert'],
  [/sprit|grappa|whisky|likör/, 'spirit'],
  [/(^|[^a-zåäö])öl([^a-zåäö]|$)/, 'beer'],
];

export function categoryKey(category: string): string {
  const text = category.toLocaleLowerCase('sv');
  const rule = CATEGORY_RULES.find(([pattern]) => pattern.test(text));
  return rule ? rule[1] : 'other';
}

export const categoryTone = (category: string): Tone => CATEGORY_TONES[categoryKey(category)];

export const pill = (tone: Tone): PillStyle => ({ fg: tone.fg, bg: tone.bg });

export const QUICK_FILTERS: Array<{ value: Exclude<AlcoholQuick, ''>; label: string }> = [
  { value: 'coming', label: 'Väntar på godkännande' },
  { value: 'notCurrent', label: 'Inte aktuella' },
  { value: 'noPairings', label: 'Saknar Passar till' },
  { value: 'longName', label: 'Namn över 35 tecken' },
  { value: 'noWinery', label: 'Saknar vingårdstext' },
];

export function counterColor(length: number, limit: number): string {
  if (length > limit) return launchColors.dangerText;
  if (length > limit * 0.9) return launchColors.warningText;
  return launchColors.textMuted;
}

export function ringColor(done: number, total: number): string {
  const share = total ? done / total : 0;
  if (share > 0.85) return launchColors.moss;
  if (share >= 0.5) return launchColors.warningMarker;
  return launchColors.dangerText;
}

const channel = (value: number) => {
  const share = value / 255;
  return share <= 0.03928 ? share / 12.92 : Math.pow((share + 0.055) / 1.055, 2.4);
};

const luminance = (hex: string) => {
  const value = hex.replace('#', '');
  const [r, g, b] = [0, 2, 4].map((start) => parseInt(value.slice(start, start + 2), 16));
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

export function contrastRatio(foreground: string, background: string): number {
  const [light, dark] = [luminance(foreground), luminance(background)].sort((left, right) => right - left);
  return (light + 0.05) / (dark + 0.05);
}

export const alcoholCommandId = () => `al-${Date.now().toString(36)}-${Math.random().toString(16).slice(2, 8)}`;
